import { add, dist, scale, viewDir } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { abilIdx, ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, applyDamage, applyStatus, damageBarrier, explode, isEnemy, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { action, climb, commonInput, consume, contactEnemies, ready, secs, simpleProjectile, startMove, trigger, useUlt } from "../kitlib.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";

export const V = { dash: 0, pounce: 1, wall: 2, low: 3 } as const;
export const PK = { Spear: pk(HeroId.BlackPanthera, 0) } as const;
export const FX = { Claw: fxk(HeroId.BlackPanthera, 0), Spear: fxk(HeroId.BlackPanthera, 1), Pounce: fxk(HeroId.BlackPanthera, 2) } as const;

const KIND_REND = MoveKind.Hero;
const KIND_KICK = MoveKind.Hero + 1;
const KIND_POUNCE = MoveKind.Hero + 2;
const MARK_SECONDS = 7;

const def: HeroDef = {
  id: HeroId.BlackPanthera,
  slug: "black-panthera",
  name: "Black Panthera",
  role: Role.Damage,
  color: "#6b4fd6",
  blurb: "Royal claw assassin who marks prey, rips through them, then resets the dive.",
  health: 275,
  armor: 0,
  shields: 125,
  height: 1.86,
  radius: 0.4,
  headRadius: 0.22,
  speed: 7,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3300,
  abilities: {
    [Slot.Primary]: { name: "Panthera Claws", key: "LMB", description: "Slash enemies in front of you for 35 damage.", cooldown: 0 },
    [Slot.Secondary]: { name: "Pride Spear", key: "RMB", description: "Throw an arcing spear that bursts, damages enemies, and marks them.", cooldown: 5, charges: 2 },
    [Slot.Ability1]: { name: "Shadow Rend", key: "Shift", description: "Dash 15 m and slash enemies. Marked hits reset this dash and grant bonus health.", cooldown: 10 },
    [Slot.Ability2]: { name: "Roundhouse Prowl", key: "E", description: "Spin forward horizontally, damaging and marking enemies you touch.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Pridefall Descent", key: "Q", description: "Pounce forward with a spectral cat, damaging and marking enemies in the path.", cooldown: 0 },
  },
  passives: [
    { name: "Low-Crowned Fury", description: "While below 100 health, your damage is increased by 20%." },
    { name: "Velvet Step", description: "Hold Jump into walls to sprint upward and set up dives." },
  ],
};

function damageMul(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  return sim.c.Health.hp[eid]! < 100 ? 1.2 : 1;
}

function mark(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, target: number) {
  applyStatus(sim, target, "marked", MARK_SECONDS, { source: owner, by: owner });
}

function isMarkedBy(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, target: number) {
  return sim.c.Status.marked[target]! > 0 && sim.c.Status.markedBy[target] === owner + 1;
}

function meleeSlash(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, base: number, range: number, markTargets: boolean, resetOnMark: boolean, fx: number) {
  const c = sim.c;
  const before = sim.posOf(eid);
  let reset = false;
  contactEnemies(sim, eid, range, (t) => {
    const marked = isMarkedBy(sim, eid, t);
    applyDamage(sim, t, base * damageMul(sim, eid), eid);
    sim.emit({ type: "impact", pos: add(sim.posOf(t), { x: 0, y: sim.hero(t).height * 0.55, z: 0 }), kind: fx });
    if (markTargets) mark(sim, eid, t);
    if (resetOnMark && marked) {
      reset = true;
      c.Status.marked[t] = 0;
      addOverhealth(sim, eid, Math.min(75, c.Health.over[eid]! + 25), secs(4), eid);
    }
  });
  if (reset) {
    c.Abil.cd[abilIdx(eid, Slot.Ability1)] = 0;
    c.Abil.charges[abilIdx(eid, Slot.Ability1)] = 1;
  }
  if (dist(before, sim.posOf(eid)) > 0.1) setHv(c, eid, V.dash, 1);
}

function startLunge(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, kind: MoveKind, inputYaw: number, seconds: number, speed: number, y = 0) {
  const d = viewDir(inputYaw, 0);
  startMove(sim, eid, kind, secs(seconds), speed, { x: d.x * speed, y, z: d.z * speed });
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      setHv(c, eid, V.dash, Math.max(0, hv(c, eid, V.dash) - 0.08));
      setHv(c, eid, V.pounce, Math.max(0, hv(c, eid, V.pounce) - 0.05));
      setHv(c, eid, V.low, c.Health.hp[eid]! < 100 ? 1 : 0);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(0.8);
        setHv(c, eid, V.pounce, 1);
        c.Abil.cd[abilIdx(eid, Slot.Ability1)] = 0;
        c.Abil.charges[abilIdx(eid, Slot.Ability1)] = 1;
        startLunge(sim, eid, KIND_POUNCE, input.yaw, 0.44, 50, 1.5);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 10);
        startLunge(sim, eid, KIND_REND, input.yaw, 0.3, 50);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 12);
        startLunge(sim, eid, KIND_KICK, input.yaw, 16 / 15, 15);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 14);
        const a = sim.c.Look;
        const dir = viewDir(a.yaw[eid]!, a.pitch[eid]!);
        const origin = add(sim.posOf(eid), { x: dir.x * 0.8, y: sim.hero(eid).height * 0.75, z: dir.z * 0.8 });
        spawnProjectile(sim, eid, { kind: PK.Spear, origin, vel: scale(dir, 60), gravity: 18, radius: 0.18, seconds: 1.2, seq: input.seq });
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = secs(0.44);
        action(sim, eid, ActionKind.Melee, 10);
        meleeSlash(sim, eid, 35, 3, false, false, FX.Claw);
      }
    },
    movement(sim, eid, input, mods) {
      const climbing = !!(input.buttons & Btn.Jump) && climb(sim, eid, input, mods, { speed: 10 });
      setHv(sim.c, eid, V.wall, climbing ? 1 : 0);
    },
    scripted(sim, eid, _input, kind) {
      if (kind === KIND_REND) meleeSlash(sim, eid, 80, 1.15, false, true, FX.Claw);
      else if (kind === KIND_KICK) meleeSlash(sim, eid, 75, 1.25, true, false, FX.Claw);
      else if (kind === KIND_POUNCE) meleeSlash(sim, eid, 150, 2.5, true, false, FX.Pounce);
    },
  },
  projectiles: {
    [PK.Spear]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!sim.players.has(owner)) return;
        sim.emit({ type: "explosion", pos: hit.pos, radius: 5, kind: FX.Spear });
        for (const { eid: t } of playersInRadius(sim, hit.pos, 5, (e) => isEnemy(sim, owner, e))) {
          applyDamage(sim, t, 50 * damageMul(sim, owner), owner, { pos: hit.pos });
          mark(sim, owner, t);
        }
        if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 50, owner);
        if (hit.construct !== undefined) explode(sim, owner, hit.pos, { radius: 5, damage: 50, fxKind: FX.Spear });
      },
      { color: "#9f7dff", size: 0.16, trail: true },
    ),
  },
  fx: {
    [FX.Claw]: { color: "#bca8ff" },
    [FX.Spear]: { color: "#8d6bff" },
    [FX.Pounce]: { color: "#ede9ff" },
  },
};

export default module;
